import React from 'react';

/**
 * WeeklyBarChart — dependency-free SVG chart. Renders `data` as vertical bars
 * with axis-hugging labels. Kept intentionally small (< 100 LOC) so we don't
 * pull in a chart library for a single chart.
 *
 * Props:
 *   data:  Array<{ label: string, value: number }>
 *   color: bar fill (defaults to Tababa blue)
 *   height: chart height in px (default 140)
 *   maxValue: force the top of the Y scale (auto-detects otherwise)
 */
export const WeeklyBarChart = ({
  data,
  color = '#1565c0',
  height = 140,
  maxValue,
  testId = 'weekly-bar-chart',
}) => {
  if (!data || data.length === 0) {
    return null;
  }
  const width = 480;
  const padX = 24;
  const padY = 22;
  const chartW = width - padX * 2;
  const chartH = height - padY * 2;
  const barW = chartW / data.length - 8;
  const top = Math.max(1, maxValue ?? Math.max(...data.map((d) => d.value), 1));

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width="100%"
      height={height}
      className="overflow-visible"
      data-testid={testId}
    >
      {/* Gridlines */}
      {[0, 0.25, 0.5, 0.75, 1].map((f) => {
        const y = padY + chartH * (1 - f);
        return (
          <line
            key={f}
            x1={padX}
            x2={width - padX}
            y1={y}
            y2={y}
            stroke="#e2e8f0"
            strokeWidth={1}
            strokeDasharray={f === 0 ? '' : '3 3'}
          />
        );
      })}

      {/* Bars */}
      {data.map((d, i) => {
        const h = chartH * (d.value / top);
        const x = padX + (chartW / data.length) * i + 4;
        const y = padY + chartH - h;
        return (
          <g key={i} data-testid={`chart-bar-${i}`}>
            <rect
              x={x}
              y={y}
              width={barW}
              height={h}
              rx={4}
              fill={color}
              opacity={0.85}
            >
              <title>{`${d.label}: ${d.value}`}</title>
            </rect>
            {/* Value on top of the bar when non-zero */}
            {d.value > 0 && (
              <text
                x={x + barW / 2}
                y={y - 4}
                textAnchor="middle"
                fontSize="11"
                fontWeight="700"
                fill={color}
              >
                {d.value}
              </text>
            )}
            {/* Week label at the bottom */}
            <text
              x={x + barW / 2}
              y={height - 6}
              textAnchor="middle"
              fontSize="10"
              fill="#64748b"
            >
              {d.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

export default WeeklyBarChart;
